import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
import { Building2, MapPin, Sparkles, X } from "lucide-react";
import Errortext from "../../Utlis/Errortext";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Hooks";
import { sendAccountInfoAPI, updateAccountInfoAPI } from "../../../../Redux/ApiCalls/Dashboard/PackageAPI";
import { verifyEmailLinkAPI } from "../../../../Redux/ApiCalls/Auth/user";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  PrimaryBtn,
  RequiredStar,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";

const breakpoints = {
  mobile: "480px",
  tablet: "768px",
  desktop: "1024px",
};
interface FormProps {
  account_number?: string;
  ifsc_code?: string;
  beneficiary_name?: string;
  pan?: string;
  uidai?: string;
  tnc_accepted?: boolean;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  zip_code?: string | number;
  country?: string;
  user_address?: {
    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    state?: string;
    zip_code?: string | number;
    country?: string;
  };
  onClose: () => void;
}
const AccountInformationForm: React.FC<FormProps> = ({
  account_number,
  ifsc_code,
  beneficiary_name,
  pan,
  uidai,
  tnc_accepted,
  address_line_1: userAddressLine1,
  address_line_2: userAddressLine2,
  city: userCity,
  state: userState,
  zip_code: userZipCode,
  country: userCountry,
  user_address,
  onClose,
}) => {
  const normalizeCountryForDropdown = (value?: string) => {
    if (!value || !value.trim()) return "India";
    return value
      .trim()
      .toLowerCase()
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const [selectedCountry, setSelectedCountry] = React.useState<string>(
    normalizeCountryForDropdown(user_address?.country ?? userCountry)
  );
  const [selectedRegion, setSelectedRegion] = React.useState<string>(
    user_address?.state ?? userState ?? ""
  );
  const [accName, setAccName] = useState<string>("");
  const [accNo, setAccNo] = useState<string>("");
  const [accIFSC, setAccIFSC] = useState<string>("");
  const [add1, setAdd1] = useState<string>("");
  const [add2, setAdd2] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [zipcode, setZipCode] = useState<string>("");
  const [panCardNumber, setPanCardNumber] = useState<string>("");
  const [uidaiNumber, setUidaiNumber] = useState<string>("");
  const [TnCChecked, setTnCChecked] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  const aadhaarRegex = /^[0-9]{12}$/;
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  useEffect(() => {
    if (account_number) {
      setAccNo(account_number);
    }
    if (ifsc_code) {
      setAccIFSC(ifsc_code);
    }
    if (beneficiary_name) {
      setAccName(beneficiary_name);
    }
    if (pan) {
      setPanCardNumber(pan);
    }
    if (uidai) {
      setUidaiNumber(uidai);
    }
    if (typeof tnc_accepted === "boolean") {
      setTnCChecked(tnc_accepted);
    }

    const resolvedAddressLine1 = user_address?.address_line_1 ?? userAddressLine1 ?? "";
    const resolvedAddressLine2 = user_address?.address_line_2 ?? userAddressLine2 ?? "";
    const resolvedCity = user_address?.city ?? userCity ?? "";
    const resolvedZipCode = user_address?.zip_code ?? userZipCode ?? "";
    const resolvedState = user_address?.state ?? userState ?? "";
    const resolvedCountry = user_address?.country ?? userCountry;

    setAdd1(resolvedAddressLine1);
    setAdd2(resolvedAddressLine2);
    setCity(resolvedCity);
    setZipCode(resolvedZipCode ? resolvedZipCode.toString() : "");
    setSelectedRegion(resolvedState);
    setSelectedCountry(normalizeCountryForDropdown(resolvedCountry));
  }, [account_number, ifsc_code, beneficiary_name, pan, uidai, tnc_accepted, user_address, userAddressLine1, userAddressLine2, userCity, userState, userZipCode, userCountry]);
  const onChangeData = (name: string, value: string) => {
    if (name === "acc_name") {
      setAccName(value);
    } else if (name === "acc_no" && value.length <= 18 && value.length >= 0) {
      setAccNo(value);
    } else if (name === "acc_ifsc") {
      setAccIFSC(value);
    } else if (name === "add1") {
      setAdd1(value);
    } else if (name === "add2") {
      setAdd2(value);
    } else if (name === "city") {
      setCity(value);
    } else if (name === "zip" && value.length < 8) {
      const sanitizedZip = value.replace(/\D/g, "");
      setZipCode(sanitizedZip);
    } else if (name === "pan") {
      const sanitizedPan = value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 10).toUpperCase();
      setPanCardNumber(sanitizedPan);
    } else if (name === "uidai") {
      const sanitizedUidai = value.replace(/\D/g, "").slice(0, 12);
      setUidaiNumber(sanitizedUidai);
    }
  };
  const handleValidation = (name: string, value: string) => {
    if (name === "acc_name") {
      return value.trim().length > 0;
    } else if (name === "acc_no") {
      return value.trim().length > 0;
    } else if (name === "acc_ifsc") {
      return value.trim().length > 0;
    } else if (name === "zip") {
      return value.trim().length > 0;
    } else if (name === "city") {
      return value.trim().length > 0;
    } else if (name === "add1") {
      return value.trim().length > 0;
    } else if (name === "add2") {
      return value.trim().length > 0;
    } else if (name === "state") {
      return value.trim().length > 0;
    } else if (name === "country") {
      return value.trim().length > 0;
    } else if (name === "pan") {
      return panRegex.test(value.trim().toUpperCase());
    } else if (name === "uidai") {
      return aadhaarRegex.test(value.trim());
    }
    return true;
  };
  const handleVerifyEmail = (email: string) => {
    dispatch(verifyEmailLinkAPI({ email }));
  };

  const handleSubmit = () => {
    const normalizedAccName = accName.trim();
    const normalizedAccNo = accNo.trim();
    const normalizedIFSC = accIFSC.trim().toUpperCase();
    const normalizedAddressLine1 = add1.trim();
    const normalizedAddressLine2 = add2.trim();
    const normalizedCity = city.trim();
    const normalizedState = selectedRegion.trim();
    const normalizedZipCode = zipcode.trim();
    const normalizedCountry = selectedCountry.trim().toUpperCase();
    const normalizedPan = panCardNumber.trim().toUpperCase();
    const normalizedUidai = uidaiNumber.trim();

    if (
      handleValidation("acc_name", normalizedAccName) &&
      handleValidation("acc_no", normalizedAccNo) &&
      handleValidation("acc_ifsc", normalizedIFSC) &&
      handleValidation("add1", normalizedAddressLine1) &&
      handleValidation("add2", normalizedAddressLine2) &&
      handleValidation("city", normalizedCity) &&
      handleValidation("zip", normalizedZipCode) &&
      handleValidation("state", normalizedState) &&
      handleValidation("country", normalizedCountry) &&
      handleValidation("pan", normalizedPan) &&
      handleValidation("uidai", normalizedUidai) &&
      TnCChecked
    ) {
      const apiPayload = {
        beneficiary_name: normalizedAccName,
        account_number: normalizedAccNo,
        ifsc_code: normalizedIFSC,
        tnc_accepted: true,
        address_line_1: normalizedAddressLine1,
        address_line_2: normalizedAddressLine2,
        city: normalizedCity,
        state: normalizedState,
        zip_code: normalizedZipCode,
        country: normalizedCountry,
        pan: normalizedPan,
        uidai: normalizedUidai,
      };

      if (account_number) {
        dispatch(updateAccountInfoAPI(apiPayload));
      } else {
        dispatch(sendAccountInfoAPI(apiPayload));
      }

      onClose();
      // account_number": "1234567890",
      // "ifsc_code": "IFSC0001234",
      //     "beneficiary_name": "John Doe",
      //         "tnc_accepted": true,
      //             "address_line_1": "123 Main St",
      //                 "address_line_2": "Suite 100",
      //                     "city": "Metropolis",
      //                         "state": "Gujarat",
      //                             "zip_code": "123456",
      //                                 "country": "India"
    } else {
      setShowError(true);
    }
  };
  if (!user) return null;
  return (
    <ModalOverlay onClick={onClose}>
      <Shell onClick={(e) => e.stopPropagation()}>
        <ModalHero>
          <HeroGlow />
          <HeroRow>
            <HeroText>
              <HeroBadge>
                <Sparkles size={10} />
                Payments setup
              </HeroBadge>
              <ModalTitle>Account Information</ModalTitle>
              <ModalSubtitle>
                Add bank and billing details to receive customer payments.
              </ModalSubtitle>
            </HeroText>
            <HeroRight>
              <VerifyPill
                type="button"
                $verified={user.is_email_verified}
                onClick={() => {
                  if (!user.is_email_verified) handleVerifyEmail(user.email);
                }}
              >
                {user?.is_email_verified ? "Email verified" : "Verify email"}
              </VerifyPill>
              <CloseBtn type="button" onClick={onClose}>
                <X size={18} />
              </CloseBtn>
            </HeroRight>
          </HeroRow>
        </ModalHero>

        <ModalBody>
          <SectionCard>
            <SectionHeader>
              <SectionIcon>
                <Building2 size={16} />
              </SectionIcon>
              <SectionHeading>Bank details</SectionHeading>
            </SectionHeader>
            <FieldsGrid>
              <FieldGroup>
                <Label htmlFor="beneficiary_name">
                  Beneficiary name <RequiredStar>*</RequiredStar>
                </Label>
                <Input
                  type="text"
                  id="beneficiary_name"
                  name="beneficiary_name"
                  value={accName}
                  onChange={(e) => onChangeData("acc_name", e.target.value)}
                />
                <Errortext
                  message="Please provide valid account name"
                  show={showError && !handleValidation("acc_name", accName)}
                />
              </FieldGroup>
              <FieldGroup>
                <Label htmlFor="account_number">
                  Account number <RequiredStar>*</RequiredStar>
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  id="account_number"
                  name="account_number"
                  value={accNo}
                  onChange={(e) => onChangeData("acc_no", e.target.value)}
                  placeholder="123456789"
                />
                <Errortext
                  message="Please provide valid account number"
                  show={showError && !handleValidation("acc_no", accNo.toString())}
                />
              </FieldGroup>
              <FieldGroup>
                <Label htmlFor="ifsc_code">
                  IFSC code <RequiredStar>*</RequiredStar>
                </Label>
                <Input
                  type="text"
                  id="ifsc_code"
                  name="ifsc_code"
                  value={accIFSC}
                  onChange={(e) => onChangeData("acc_ifsc", e.target.value)}
                />
                <Errortext
                  message="Please provide valid IFSC code"
                  show={showError && !handleValidation("acc_ifsc", accIFSC)}
                />
              </FieldGroup>
            </FieldsGrid>
          </SectionCard>

          <SectionCard>
            <SectionHeader>
              <SectionIcon>
                <MapPin size={16} />
              </SectionIcon>
              <SectionHeading>Billing address</SectionHeading>
            </SectionHeader>
            <FieldsGrid>
              <FieldGroup className="full-width">
                <Label htmlFor="address_line_1">
                  Address line 1 <RequiredStar>*</RequiredStar>
                </Label>
                <Input
                  type="text"
                  id="address_line_1"
                  name="address_line_1"
                  value={add1}
                  onChange={(e) => onChangeData("add1", e.target.value)}
                />
                <Errortext
                  message="Please provide valid address line 1"
                  show={showError && !handleValidation("add1", add1)}
                />
              </FieldGroup>
              <FieldGroup className="full-width">
                <Label htmlFor="address_line_2">
                  Address line 2 <RequiredStar>*</RequiredStar>
                </Label>
                <Input
                  type="text"
                  id="address_line_2"
                  name="address_line_2"
                  value={add2}
                  onChange={(e) => onChangeData("add2", e.target.value)}
                />
                <Errortext
                  message="Please provide valid address line 2"
                  show={showError && !handleValidation("add2", add2)}
                />
              </FieldGroup>
              <FieldGroup>
                <Label htmlFor="country">
                  Country <RequiredStar>*</RequiredStar>
                </Label>
                <StyledCountryDropdown
                  value={selectedCountry || "India"}
                  onChange={(val) => {
                    setSelectedCountry(val);
                    setSelectedRegion("");
                  }}
                />
                <Errortext
                  message="Please provide country"
                  show={showError && !handleValidation("country", selectedCountry)}
                />
              </FieldGroup>
              <FieldGroup>
                <Label htmlFor="state">
                  State/Region <RequiredStar>*</RequiredStar>
                </Label>
                <StyledRegionDropdown
                  country={selectedCountry || ""}
                  value={selectedRegion || ""}
                  onChange={(val) => setSelectedRegion(val)}
                />
                <Errortext
                  message="Please provide state"
                  show={showError && !handleValidation("state", selectedRegion)}
                />
              </FieldGroup>
              <FieldGroup>
                <Label htmlFor="city">
                  City <RequiredStar>*</RequiredStar>
                </Label>
                <Input
                  type="text"
                  id="city"
                  value={city}
                  name="city"
                  onChange={(e) => onChangeData("city", e.target.value)}
                />
                <Errortext
                  message="Please provide city"
                  show={showError && !handleValidation("city", city)}
                />
              </FieldGroup>
              <FieldGroup>
                <Label htmlFor="zip_code">
                  ZIP code <RequiredStar>*</RequiredStar>
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  id="zip_code"
                  name="zip_code"
                  value={zipcode}
                  onChange={(e) => onChangeData("zip", e.target.value)}
                />
                <Errortext
                  message="Please provide ZIP code"
                  show={showError && !handleValidation("zip", zipcode)}
                />
              </FieldGroup>
              <FieldGroup>
                <Label htmlFor="pan_card_number">
                  PAN card <RequiredStar>*</RequiredStar>
                </Label>
                <Input
                  type="text"
                  id="pan_card_number"
                  name="pan_card_number"
                  value={panCardNumber}
                  onChange={(e) => onChangeData("pan", e.target.value)}
                  placeholder="ABCDE1234F"
                />
                <Errortext
                  message="Please provide a valid PAN (ABCDE1234F)"
                  show={showError && !handleValidation("pan", panCardNumber)}
                />
              </FieldGroup>
              <FieldGroup>
                <Label htmlFor="uidai_number">
                  Aadhaar card <RequiredStar>*</RequiredStar>
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  id="uidai_number"
                  name="uidai_number"
                  value={uidaiNumber}
                  onChange={(e) => onChangeData("uidai", e.target.value)}
                  placeholder="123412341234"
                />
                <Errortext
                  message="Please provide a valid 12-digit Aadhaar number"
                  show={showError && !handleValidation("uidai", uidaiNumber)}
                />
              </FieldGroup>
              <FieldGroup className="full-width">
                <CheckboxContainer>
                  <CheckboxInput
                    type="checkbox"
                    id="tnc_accepted"
                    name="tnc_accepted"
                    checked={TnCChecked}
                    onChange={() => setTnCChecked((pre) => !pre)}
                  />
                  <CheckboxLabel htmlFor="tnc_accepted">
                    I accept the terms and conditions <RequiredStar>*</RequiredStar>
                  </CheckboxLabel>
                </CheckboxContainer>
                <Errortext
                  message="Please accept the terms and conditions"
                  show={showError && !TnCChecked}
                />
              </FieldGroup>
            </FieldsGrid>
          </SectionCard>
        </ModalBody>

        <ModalFooter>
          <SubmitBtn type="button" onClick={() => handleSubmit()}>
            {account_number ? "Update account" : "Save account"}
          </SubmitBtn>
        </ModalFooter>
      </Shell>
    </ModalOverlay>
  );
};

export default AccountInformationForm;

const Shell = styled(ModalShell)`
  max-width: 720px;
`;

const ModalFooter = styled.div`
  flex-shrink: 0;
  padding: 16px 24px 24px;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
  background: #fff;

  @media (max-width: ${breakpoints.tablet}) {
    padding: 14px 16px 18px;
  }
`;

const SubmitBtn = styled(PrimaryBtn)`
  width: 100%;
  min-height: 46px;
  flex-shrink: 0;
  white-space: nowrap;
`;

const HeroRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const VerifyPill = styled.button<{ $verified: boolean }>`
  padding: 7px 12px;
  border-radius: 999px;
  border: 1px solid
    ${(p) => (p.$verified ? "rgba(49,166,106,0.3)" : "rgba(228,72,98,0.3)")};
  background: ${(p) =>
    p.$verified ? "rgba(49,166,106,0.1)" : "rgba(228,72,98,0.1)"};
  color: ${(p) => (p.$verified ? "#31a66a" : "#e44862")};
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  cursor: ${(p) => (p.$verified ? "default" : "pointer")};
  white-space: nowrap;
`;

const SectionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 18px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.04);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SectionIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c94daa;
  background: rgba(201, 77, 170, 0.1);
`;

const SectionHeading = styled.h3`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 800;
  color: #171717;
`;

const Label = styled.label`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 44px;
  padding: 0 14px;
  color: #171717;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  background-color: #fafafa;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    outline: none;
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }

  &::placeholder {
    color: rgba(23, 23, 23, 0.35);
    font-weight: 500;
  }

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 13px;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  border-radius: 12px;
  background-color: #fafafa;
`;

const CheckboxInput = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #c94daa;
`;

const CheckboxLabel = styled.label`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #171717;
`;

const StyledDropdownStyles = `
  width: 100%;
  box-sizing: border-box;
  height: 44px;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background-color: #fafafa;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #171717;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 13px;
  }
`;

const StyledRegionDropdown = styled(RegionDropdown)`
  ${StyledDropdownStyles}
`;

const StyledCountryDropdown = styled(CountryDropdown)`
  ${StyledDropdownStyles}
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px 16px;

  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  &.full-width {
    grid-column: 1 / -1;
  }
`;
